import type {
  CustomizationSettings,
  ExtraButtonsSettings,
  PrivateModeSettings,
  RasiTechModulesSettings,
} from '../global/types/rasiTechModules';
import { DEFAULT_RASITECH_MODULES } from '../global/types/rasiTechModules';

const STORAGE_KEY = 'rasiTechModules';

let cachedModules: RasiTechModulesSettings = loadStored();

function loadStored(): RasiTechModulesSettings {
  if (typeof localStorage === 'undefined') {
    return { ...DEFAULT_RASITECH_MODULES };
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return deepMerge(DEFAULT_RASITECH_MODULES, JSON.parse(raw) as Partial<RasiTechModulesSettings>);
    }
  } catch {
    // ignore
  }
  return { ...DEFAULT_RASITECH_MODULES };
}

function deepMerge<T extends object>(base: T, patch: Partial<T>): T {
  const out = { ...base };
  Object.keys(patch).forEach((key) => {
    const k = key as keyof T;
    const val = patch[k];
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      out[k] = deepMerge(base[k] as object, val as object) as T[keyof T];
    } else if (val !== undefined) {
      out[k] = val as T[keyof T];
    }
  });
  return out;
}

function persist() {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cachedModules));
  }
}

export function setRasiTechModulesCache(patch: Partial<RasiTechModulesSettings>) {
  cachedModules = deepMerge(cachedModules, patch);
  persist();
}

export function getRasiTechModules(): RasiTechModulesSettings {
  return cachedModules;
}

export function getPrivateModeSettings(): PrivateModeSettings {
  return cachedModules.privateMode;
}

export function isPrivateModeEnabled<K extends keyof PrivateModeSettings>(key: K): boolean {
  return Boolean(cachedModules.privateMode[key]);
}

export function getCustomizationSettings(): CustomizationSettings {
  return cachedModules.customizations;
}

export function isExtraButtonEnabled<K extends keyof ExtraButtonsSettings>(key: K): boolean {
  return Boolean(cachedModules.extraButtons[key]);
}
